import { useCallback, useEffect, useRef, useState } from 'react';
import { ACCEPT, MAX_FILES, problemWith, sizeText, upload, viewUrl, fileUrl, type FileRef } from '../api/files';
import { Ic } from '../st8n-tickets/kit.jsx';
import { Sheet } from './sheet';

interface Item { key: string; file: File; preview: string | null; state: 'uploading' | 'done' | 'error'; progress: number; ref?: FileRef; error?: string }

/**
 * Files waiting to go with a message: pick (camera or library), upload each as soon as it is chosen (with progress), keep the ids.
 * `portal`: a customer's door (`/portal/files`), else the team's (`/files`). `ids` are the finished uploads, in order; `busy` while any is going.
 */
export function useAttachments(portal: boolean) {
  const [items, setItems] = useState<Item[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const seq = useRef(0);
  const live = useRef(true);
  useEffect(() => () => { live.current = false; }, []);
  const patch = (key: string, p: Partial<Item>) => live.current && setItems((a) => a.map((x) => (x.key === key ? { ...x, ...p } : x)));
  const send = useCallback((key: string, file: File) => {
    patch(key, { state: 'uploading', progress: 0, error: undefined });
    upload(portal, file, (progress) => patch(key, { progress })).then((ref) => patch(key, { state: 'done', progress: 1, ref }), (e: Error) => patch(key, { state: 'error', error: e.message }));
  }, [portal]); // eslint-disable-line react-hooks/exhaustive-deps
  const add = useCallback((list: FileList | File[] | null) => {
    if (!list) return;
    const files = [...list];
    setProblem(null);
    setItems((cur) => {
      const room = MAX_FILES - cur.length;
      const next: Item[] = [];
      let say: string | null = files.length > room ? `At most ${MAX_FILES} files on one message.` : null;
      for (const file of files.slice(0, Math.max(0, room))) {
        const bad = problemWith(file);
        if (bad) { say = bad; continue; }
        const key = `a${++seq.current}`;
        next.push({ key, file, preview: file.type.startsWith('image/') && !/heic|heif/.test(file.type) ? URL.createObjectURL(file) : null, state: 'uploading', progress: 0 });
        queueMicrotask(() => send(key, file));
      }
      if (say) queueMicrotask(() => setProblem(say));
      return [...cur, ...next];
    });
  }, [send]);
  const remove = (key: string) => { setProblem(null); setItems((a) => { const x = a.find((y) => y.key === key); if (x?.preview) URL.revokeObjectURL(x.preview); return a.filter((y) => y.key !== key); }); };
  const retry = (key: string) => { const x = items.find((y) => y.key === key); if (x) send(key, x.file); };
  const clear = () => { setItems((a) => { a.forEach((x) => x.preview && URL.revokeObjectURL(x.preview)); return []; }); setProblem(null); };
  return {
    items, problem, add, remove, retry, clear,
    ids: items.filter((x) => x.state === 'done' && x.ref).map((x) => x.ref!.id),
    busy: items.some((x) => x.state === 'uploading'),
    failed: items.some((x) => x.state === 'error'),
    full: items.length >= MAX_FILES,
  };
}
export type Attachments = ReturnType<typeof useAttachments>;

const Clip = () => <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m21 11.5-8.6 8.6a5.5 5.5 0 0 1-7.8-7.8l8.6-8.6a3.7 3.7 0 0 1 5.2 5.2l-8.6 8.6a1.8 1.8 0 0 1-2.6-2.6l7.9-7.9" /></svg>;

/** The button that opens the phone's chooser: a photo from the camera or library, or a PDF. Add `label` for a wide button; else a 44px icon. */
export function AttachButton({ att, label, className = '', testId = 'attach' }: { att: Attachments; label?: string; className?: string; testId?: string }) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <input ref={input} type="file" accept={ACCEPT} multiple hidden onChange={(e) => { att.add(e.target.files); e.target.value = ''; }} data-testid={`${testId}-input`} />
      <button type="button" className={label ? `btn-line ${className}` : `icon-btn ${className}`} disabled={att.full} onClick={() => input.current?.click()} aria-label={label ? undefined : 'Attach a photo or file'} data-testid={testId}><Clip />{label ? <span>{label}</span> : null}</button>
    </>
  );
}

/** The files chosen so far: a thumbnail (or a PDF chip) each, with progress while it uploads, Retry when it fails, and a remove button. */
export function AttachTray({ att }: { att: Attachments }) {
  if (!att.items.length && !att.problem) return null;
  return (
    <div data-testid="attach-tray">
      {att.items.length ? (
        <ul className="flex flex-wrap gap-2" aria-label="Files to send">
          {att.items.map((x) => (
            <li key={x.key} className="relative h-[76px] w-[76px] overflow-hidden rounded-2xl border border-line bg-panel-2" data-testid={`attach-${x.state}`} data-name={x.file.name}>
              {x.preview ? <img src={x.preview} alt="" className="h-full w-full object-cover" /> : <span className="flex h-full w-full flex-col items-center justify-center gap-1 px-1 text-center text-[11px] text-dim"><Ic.Note width={22} height={22} /><span className="line-clamp-2 break-all">{x.file.name}</span></span>}
              {x.state === 'uploading' ? (
                <span className="absolute inset-x-0 bottom-0 h-1.5 bg-black/20" role="progressbar" aria-label={`Uploading ${x.file.name}`} aria-valuenow={Math.round(x.progress * 100)} aria-valuemin={0} aria-valuemax={100}><span className="block h-full bg-accent transition-all" style={{ width: `${Math.round(x.progress * 100)}%` }} /></span>
              ) : null}
              {x.state === 'error' ? <button type="button" className="absolute inset-0 flex items-center justify-center bg-bad-soft/90 text-[12px] font-semibold text-bad-text" onClick={() => att.retry(x.key)} aria-label={`Try ${x.file.name} again`}>Retry</button> : null}
              <button type="button" className="absolute top-0 right-0 flex h-11 w-11 items-start justify-end p-1" onClick={() => att.remove(x.key)} aria-label={`Remove ${x.file.name}`}><span className="flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white"><Ic.Close width={13} height={13} /></span></button>
            </li>
          ))}
        </ul>
      ) : null}
      {att.items.some((x) => x.state === 'error') ? <p className="mt-1.5 text-[13px] text-bad-text" role="alert" data-testid="attach-message">{att.items.find((x) => x.state === 'error')?.error}</p> : null}
      {att.problem ? <p className="mt-1.5 text-[13px] text-bad-text" role="alert" data-testid="attach-problem">{att.problem}</p> : null}
    </div>
  );
}

/** Files already on a message, request or ticket: images as thumbnails that open full size, everything else as a download row. */
export function FileThumbs({ files, portal, className = '' }: { files: FileRef[]; portal: boolean; className?: string }) {
  const [open, setOpen] = useState<FileRef | null>(null);
  if (!files.length) return null;
  const images = files.filter((f) => f.image);
  const others = files.filter((f) => !f.image);
  return (
    <div className={className} data-testid="files">
      {images.length ? (
        <ul className="flex flex-wrap gap-2">
          {images.map((f) => (
            <li key={f.id}>
              <button type="button" className="block h-[88px] w-[88px] overflow-hidden rounded-2xl border border-line bg-panel-2" onClick={() => setOpen(f)} aria-label={`View ${f.name}`} data-testid={`file-${f.id}`}>
                <img src={viewUrl(portal, f)} alt={f.name} loading="lazy" className="h-full w-full object-cover" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {others.length ? (
        <ul className={`${images.length ? 'mt-2' : ''} grid gap-1.5`}>
          {others.map((f) => (
            <li key={f.id}>
              <a className="flex min-h-11 items-center gap-2.5 rounded-xl bg-panel-2 px-3 text-[14px] hover:bg-panel" href={fileUrl(portal, f.id, true)} download={f.name} data-testid={`file-${f.id}`}>
                <Ic.Note width={18} height={18} className="shrink-0 text-dim" /><span className="min-w-0 flex-1 truncate">{f.name}</span><span className="shrink-0 text-[12px] text-faint">{sizeText(f.size)} · Download</span>
              </a>
            </li>
          ))}
        </ul>
      ) : null}
      {open ? (
        <Sheet title={open.name} sub={sizeText(open.size)} onClose={() => setOpen(null)} testId="file-viewer"
          footer={<a className="btn-line" href={fileUrl(portal, open.id, true)} download={open.name}>Download</a>}>
          <img src={viewUrl(portal, open)} alt={open.name} className="mx-auto max-h-[70dvh] w-auto max-w-full rounded-2xl object-contain" data-testid="file-full" />
        </Sheet>
      ) : null}
    </div>
  );
}
